import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchCollectionWithPost } from '@app/src/api/fetchHooks'
import { useAuthentication } from '@app/src/context/AuthenticationContext'
import type { FilterGroup } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import type { ResponseItem } from '@app/src/types/resourceExplorer'
import { ONE_MINUTE_MS, Solutions } from '@app/src/wf-constants'

interface UseAssessmentResponseItemsParams {
  periodFilterValue: string | undefined
  questionnaireTemplateIds: number[]
  providerId: number | undefined
  isScreenedFilter?: boolean
  latestSubmittedOnly?: boolean
  include: string[]
  enabled?: boolean
}

export function useAssessmentResponseItems({
  periodFilterValue,
  questionnaireTemplateIds,
  providerId,
  include,
  isScreenedFilter,
  latestSubmittedOnly = true,
  enabled = true,
}: UseAssessmentResponseItemsParams) {
  const { scope } = useAuthentication()
  const isTransparency = scope.solution === Solutions.Transparency

  const filters: FilterGroup[] = [
    ...(latestSubmittedOnly
      ? [{ name: 'response.isLatestSubmitted', filters: [{ operator: Operators.EqualTo, value: true }] }]
      : []),
    {
      name: 'response.request.periodName',
      filters: [{ operator: Operators.EqualTo, value: periodFilterValue }],
    },
    {
      name: 'response.request.questionnaireTemplateId',
      filters: [{ operator: Operators.In, value: questionnaireTemplateIds }],
    },
    ...(isScreenedFilter !== undefined
      ? [{ name: 'response.isScraped', filters: [{ operator: Operators.EqualTo, value: isScreenedFilter }] }]
      : []),
    ...(!isTransparency
      ? [
          {
            name: 'response.request.subscriptions.target.id',
            filters: [{ operator: Operators.EqualTo, value: providerId }],
          } satisfies FilterGroup,
        ]
      : []),
  ]

  const canFetch =
    enabled && Boolean(questionnaireTemplateIds.length && periodFilterValue && (isTransparency || providerId))

  return useFetchCollectionWithPost<ResponseItem>({
    key: FetchKey.ResponseItemsCollection,
    endpoint: endpoints.responseItemsCollection,
    payload: { filter: filters, include },
    options: { enabled: canFetch, staleTime: ONE_MINUTE_MS },
  })
}
